import { IMeetingType, IDefaultMeetingType, IWorkDaysSchema } from '@app/core/interfaces';
import { IMeetingTypeCommonSchema } from 'common-lib';

export interface IUserMeetingSettings {
    /**
     * Settings for each meeting type.
     * If a meeting type is not present or a field inside that meeting type is not present, we should default the `defaultType[field]`, to find a default setting for that `field`.
     */
    types: { [path in string]: IMeetingType };

    /**
     * General meeting settings that are applicable to all meeting types.
     */
    defaultType: IDefaultMeetingType;

    days: IWorkDaysSchema;
}

export const AppSumoStateRedeemed = 'redeemed';
export const AppSumoStateWelcomeDismissed = 'welcome-dimissed';

export type AppSumoState = typeof AppSumoStateRedeemed | typeof AppSumoStateWelcomeDismissed;

const emailTemplateTypes = ['meetingRequest'] as const;
type emailTemplateTypesType = typeof emailTemplateTypes[number];

/**
 * 'UID-XXX' **LEGACY** format
 * This ID format is used by most of the v1 APIs including Marketplace API
 */
export type VendastaLegacyUserId = string;
/**
 * 'U-XXX' **NEW** format
 * This ID format will be used on v2 APIs and is used in the new SSO flow
 */
export type VendastaUserId = string;

/** This is IUserSchema on the BE */
export interface IUser {
    vendastaLegacyUserId?: VendastaLegacyUserId;
    vendastaUserId?: VendastaUserId;
    vendastaTopNavUrl?: string;
    versions?: {
        nlp?: '1' | '2';
        beta?: boolean;
        trial?: {
            expiry: Date;
            daysRemaining?: number;
        };
        appSumo?: AppSumoState;
    };
    restrictedApps: [];
    // appSettings: [];
    avatar: string;
    avatar_image: number;
    logoUrl?: string;
    branding?: { showZoomTaglines?: boolean; iconUrl?: string };
    country: string;
    crm: {};
    currency: string;
    dateAdded: string;
    dateLastLogin: string;
    dateUpdated: string;
    email: string;
    extraEmails: Array<string>;
    hideAutomatedAssistant: boolean;
    id: string;
    inCodeTrial: boolean;
    isProcessingOnboarding: boolean;
    language: string;
    location?: {
        work?: {
            formatted_address?: string; // e.g. 12 Selby St, Toronto, ON M4Y 0E6, Canada
            address_components?: Array<any>; // e.g. [{long_name: "12", short_name: "12", types: ["street_number"]},…]
            geometry?: any; // e.g. {location: {lat: 43.67121059999999, lng: -79.3768812}, location_type: "ROOFTOP",…}
            types?: Array<string>; // e.g. ["street_address"]
        };
    };
    managed: {
        named: {
            coffee: boolean;
            call: boolean;
        };
        timezone: boolean;
        meeting_length: boolean;
        days: boolean;
        workday: boolean;
        hourly_offsets: boolean;
        cc_meeting: boolean; // CC user when they send meeting request
    };
    meeting: IUserMeetingSettings;
    messaging: Array<{
        id: string; // e.g. andy@workbetter.ai,
        _id: string; // e.g. 123abc456
        email?: string;
        type: string; // e.g. outlook
        meta: {
            token: string;
        };
    }>;
    name: string;
    onboardingState: {}[];
    orgId?: string;
    associatedOrgId?: string;
    plan: string;
    preferredNotificationIndex: number;
    preferredNotificationType:
        | 'email'
        | 'stride'
        | 'msteams'
        | 'slack'
        | 'spark'
        | 'workspaces'
        | 'hangouts'
        | 'msteams'
        | string;
    rooms: {};
    showcontact: {
        email: boolean;
    };
    social: [];
    stats: {};
    stripeCustomer: string;
    stripeSubscription: string;
    tags: string[];
    telephones: string[];
    timezone: string;
    token: string;
    travel: {};
    info?: {
        role?: string;
        company_size?: string;
        telephone?: string;
        seniority?: string;
    };
    // TEMP: We really need to organize IUser - the below setting is for the Assistant setting
    email_footer?: any;
    templates?: { [templateType in emailTemplateTypesType]?: { isEnabled: boolean; emailTemplate: string } };
}

/** These User Setting interfaces are helper models for the FE only */
export type UserSettings = Partial<Pick<IUser, 'branding' | 'email' | 'extraEmails' | 'location' | 'name' | 'info'>>;
export type UserProfileSettings = Pick<UserSettings, 'name' | 'location' | 'info'>;

export type NewUserFormat = Omit<IUser, 'meeting'> & {
    meeting: Pick<IUser['meeting'], 'defaultType' | 'types' | 'days'>;
};

// The following are part of the unreleased Meeting Types v2 rework //
/**
 * { [meetingTypeId:string]: BaseModelSchema<IMeetingTypeSchema> }
 */
export type NewUserFormatWithMeetingTypes = NewUserFormat & {
    meetingTypes: Record<string, BaseModelSchema<IMeetingTypeCommonSchema>>;
};

export interface IBaseModelSchema {
    id: string;
    dateAdded: Date;
    dateUpdated: Date;
}

export type BaseModelSchema<T> = T & IBaseModelSchema;
// end of Meeting Types v2 work //

export enum UserAccess {
    User = 'user',
    Admin = 'admin'
}

export enum UserSeat {
    Free = 'free',
    Team = 'team'
}

/** FE-only model for making reading userAccess more convenient */
export interface IOrgUser extends IUser {
    access?: UserAccess;
    seat?: UserSeat;
}
